'use strict'

const isOwn = ({ loggedInUser, params }) => loggedInUser.username === params.username;

export const roles = {
	sysAdmin: {
		label: 'System Admin',
		inherits: [ 'admin' ],
		can: [
			'viewPlaces',
			'createPlace',
		],
	},
	admin: {
		label: 'Admin',
		inherits: [ 'manager' ],
		can: [
			'userRoute:edit',
			'userSettings:view',
			'userSettings:edit',
			'createUser',
			'adminUserEdit',
		],
	},
	dispatch: {
		label: 'Dispatch',
		inherits: [ 'manager' ],
		can: [
		],
	},
	manager: {
		label: 'Manager',
		can: [
			'userPage:view',
			'userRoute:view',
			'searchUsers',
			'searchPlaces',
			'importJobs',
			'assignJobs',
			'viewJobs',
		],
		inherits: [ 'server' ],
	},
	server: {
		label: 'Process Server',
		inherits: [ 'messenger' ],
		can: [
		],
	},
	messenger: {
		label: 'Messenger',
		can: [
			{
				name: 'userPage:view',
				when: isOwn,
			},
			{
				name: 'userRoute:view',
				when: isOwn,
			},
			{
				name: 'userRoute:edit',
				when: isOwn,
			},
			'attemptJob',
			'editAttempt',
			'deleteAttempt',
			{
				name: 'userSettings:view',
				when: isOwn,
			},
			{
				name: 'userSettings:edit',
				when: isOwn,
			},
			{
				name: 'importJobs',
				when: isOwn,
			},
		],
		inherits: [ 'guest' ],
	},
	guest: {
		label: 'Guest',
		can: [
			'viewJobs',
			'findAddresses',
			'getNav',
		],
    },
};

class Role {

	constructor(name, config, controller) {
		this.name = name;
		this.parents = config.inherits || [];
		this.controller = controller;
		this.capabilities = this.setup(config);
	}

	setup(config) {

		return config.can.reduce(
			(caps, capItem) => {
				if (typeof capItem === 'string') {
					return { ...caps, [ capItem ]: () => true };
				} else if (
					typeof capItem.name === 'string' &&
					typeof capItem.when === 'function'
				) {
					return {
						...caps,
						[ capItem.name ]: capItem.when,
					};
				}
				return caps;
			},
			{}
		);
	}

	can(action, ctx) {

		if (action in this.capabilities && this.capabilities[ action ](ctx)) {
			return true;
		}

		if (this.parents.length) {
			return this.parents.some(
				parentName => this.controller.getRole(parentName).can(action, ctx)
			);
		}

		return false;
	}
}

export class RBAC {

	constructor(roles) {
		this.roles = this.init(roles);
	}

	init(roles) {
		return Object.fromEntries(
			Object.entries(roles).reduce(
				(all, [ roleName, config ]) => ([
					...all,
					[
						roleName,
						new Role(roleName, config, this),
					],
				]),
				[]
			)
		);
	}

	getRole(roleName) {
		return this.roles[ roleName ];
	}

	userCan(user, action, ctx) {
		return user.roles.some(
			roleName => {
				const role = this.getRole(roleName);

				if (!role) {
					return false;
				}

				return role.can(action, ctx);
			}
		);
	}
};

const accRoles = roleId => {

	const role = roles[ roleId ];

	if (role.inherits && role.inherits.length) {
		return role.inherits.reduce(
			(acc, id) => [
				...acc,
				...accRoles(id),
			],
			[ roleId ]
		);
	}

	return [ roleId ];
};

export const getDerivedRoles = user => [
	...new Set(
		user.roles.reduce(
			(acc, roleId) => [
				...acc,
				...accRoles(roleId),
			],
			[]
		)
	)
];
